import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mic, LogOut, User, BookOpen, Settings, Menu, X } from 'lucide-react';

export default function Navigation({ user, onLogout, isAdmin }) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Desktop active state style
  const isActive = (path) => location.pathname === path
    ? 'border-primary-500 text-gray-900'
    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700';

  // Mobile active state style (border-left instead of border-bottom)
  const isMobileActive = (path) => location.pathname === path
    ? 'bg-indigo-50 border-primary-500 text-primary-700'
    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700';

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Logo Area */}
            <div className="flex-shrink-0 flex items-center">
              <img
                src={`${process.env.PUBLIC_URL}/favicon.svg`}
                alt="Wyrm logo"
                className="h-8 w-8 mr-2"
              />
              <span className="text-xl font-bold text-gray-900">Wyrm</span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link to="/" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive('/')}`}>
                <Mic className="w-4 h-4 mr-2" />
                Dictation
              </Link>
              <Link to="/meetings" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive('/meetings')}`}>
                <BookOpen className="w-4 h-4 mr-2" />
                Meetings
              </Link>
              <Link to="/dictionary" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive('/dictionary')}`}>
                Dictionary
              </Link>
              <Link to="/settings" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive('/settings')}`}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Link>
              {isAdmin && (
                <Link to="/admin" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive('/admin')}`}>
                  Admin
                </Link>
              )}
            </div>
          </div>

          {/* User Menu (Desktop) */}
          <div className="hidden sm:flex sm:items-center">
            <div className="flex items-center text-sm text-gray-500 mr-4">
              <User className="h-4 w-4 mr-2" />
              <span className="font-medium">{user?.name || user?.email}</span>
            </div>
            <button
              onClick={onLogout}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 focus:outline-none transition"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Logout
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden" id="mobile-menu">
          <div className="pt-2 pb-3 space-y-1">
            <Link
              to="/"
              onClick={closeMobileMenu}
              className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isMobileActive('/')}`}
            >
              <div className="flex items-center">
                <Mic className="w-4 h-4 mr-2" />
                Dictation
              </div>
            </Link>
            <Link
              to="/meetings"
              onClick={closeMobileMenu}
              className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isMobileActive('/meetings')}`}
            >
              <div className="flex items-center">
                <BookOpen className="w-4 h-4 mr-2" />
                Meetings
              </div>
            </Link>
            <Link
              to="/dictionary"
              onClick={closeMobileMenu}
              className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isMobileActive('/dictionary')}`}
            >
              <div className="flex items-center">
                Dictionary
              </div>
            </Link>
            <Link
              to="/settings"
              onClick={closeMobileMenu}
              className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isMobileActive('/settings')}`}
            >
              <div className="flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </div>
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                onClick={closeMobileMenu}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isMobileActive('/admin')}`}
              >
                Admin
              </Link>
            )}
          </div>
          <div className="pt-4 pb-4 border-t border-gray-200">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-500" />
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">{user?.name || 'User'}</div>
                <div className="text-sm font-medium text-gray-500">{user?.email}</div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <button
                onClick={() => {
                  closeMobileMenu();
                  onLogout();
                }}
                className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
              >
                <div className="flex items-center">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
